import React, { useContext, useState, useMemo } from 'react';
// import { useRouteMatch } from 'react-router';
import {
  Grid,
  CircularProgress,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from '@material-ui/core';
import { useClasses, useMTSDeleteEntry } from '../../../hooks';
import { MTSContext, SiteContext, UserContext } from '../../../contexts';
import { MTSToolBar } from '../MTSToolBar';
import { CalendarCustomReportingRow } from './CalendarCustomReportingRow';
import { MTSFooter } from '../MTSFooter';
import MTSHelpers, { getTypesNeedingApproval } from '../../../helpers/mtsHelpers';
import { LanguageString, DialogTitle, GeneralErrorMessage } from '../../Common';
import * as DTO from '../../../dto';
import { MTSAddActivityDialog } from '../MTSAddActivity/MTSAddActivityDialog';
import { ApolloError } from 'apollo-client';
import { Alert } from '@material-ui/lab';
import { CalendarMonthSubmissionStatus } from '../CalendarMonthView/CalendarMonthSubmissionStatus';
import MTSSubmitDialog from '../MTSReviewHoursDialog/MTSSubmitDialog';
import MTSApproveDialog from '../MTSReviewHoursDialog/MTSApproveDialog';

export const CalendarCustomReportingView: React.FC = () => {
  const classes = useClasses();
  const mtsCtx = useContext(MTSContext);
  const userCtx = useContext(UserContext);
  const site = useContext(SiteContext);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [readonlyMode, setReadonlyMode] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [activeMTSEntry, setActiveMTSEntry] = useState<DTO.MTSActivity | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [deleteMTS] = useMTSDeleteEntry();
  const mtses = mtsCtx.mtses ?? [];
  const activityTypes: DTO.MTSActivityType[] = mtsCtx.activityTypes ?? [];

  const date = mtsCtx.mtsPeriod?.startDate ? MTSHelpers.dateFromString(mtsCtx.mtsPeriod.startDate) : new Date();
  const activities = mtses.map(mts => mts.entries?.entries ?? []).flat();

  const typesNeedingApproval = mtsCtx.selectedMTS ? getTypesNeedingApproval(mtsCtx.selectedMTS) : undefined;

  const activteMts = useMemo(() => {
    return mtses.find(mts => mts.id === activeMTSEntry?.mtsId);
  }, [activeMTSEntry, mtses]);


  const formEditDisabled = useMemo(() => {

    if (userCtx.user?.roles?.includes(DTO.Role.ADMIN) ||
      userCtx.user?.roles?.includes(DTO.Role.ADVISOR) ||
      userCtx.user?.roles?.includes(DTO.Role.SUPERADMIN)
    ) return false;

    if (mtsCtx.selectedMTS?.state === DTO.MTSState.SUBMITTED || mtsCtx.selectedMTS?.state === DTO.MTSState.APPROVED) {
      return true;
    }

    return false;
  }, [userCtx.user?.currentRole, readonlyMode, mtsCtx.selectedMTS?.state]);

  const formHoursDisabled = useMemo(() => {

    if (userCtx.user?.roles?.includes(DTO.Role.ADMIN) ||
      userCtx.user?.roles?.includes(DTO.Role.ADVISOR) ||
      userCtx.user?.roles?.includes(DTO.Role.SUPERADMIN)
    ) return false;

    if (mtsCtx.selectedMTS?.state === DTO.MTSState.SUBMITTED || mtsCtx.selectedMTS?.state === DTO.MTSState.APPROVED) {
      return true;
    }

    return false;
  }, [userCtx.user?.currentRole, readonlyMode, mtsCtx.selectedMTS?.state]);

  const toggleDeleteModal = () => {
    setDeleteModalOpen(!deleteModalOpen);
  };

  const confirmDeleteMTS = () => {
    setDeleteModalOpen(false);
    if (activeMTSEntry) {
      toggleDeleteModal();

      deleteMTS({
        variables: {
          id: activeMTSEntry.id
        },
        update: (cache, result) => {
          setActiveMTSEntry(undefined);
        },
        refetchQueries: ['mtsEntries', 'mtsActivityType']
      }).catch(res => {
        const errors: string[] = res.graphQLErrors.map((error: ApolloError) => {
          return error.message;
        });

        if (errors.length > 0) {
          setErrorMessage(errors[0]);
        }
      });
    }
  };

  const toggleAddModal = () => {
    setReadonlyMode(false);
    if (addModalOpen) {
      setActiveMTSEntry(undefined);
    }
    setAddModalOpen(!addModalOpen);
  };

  const toggleSubmitModal = () => {
    setSubmitModalOpen(!submitModalOpen);
  };

  const toggleApproveModal = () => {
    setApproveModalOpen(!approveModalOpen);
  };

  const canSeeReviewMTS = useMemo(() => {
    if (site.site?.showCustomReportingSubmit) {
      if (userCtx.user && site.site && mtsCtx.selectedMTS) {
        return MTSHelpers.canReviewMTS(userCtx.user, mtsCtx.selectedMTS);
      }
    }

    return false;
  }, [userCtx.user, mtsCtx.selectedMTS]);

  const canSeeApproveMTS = useMemo(() => {
    if (site.site?.showCustomReportingSubmit) {
      if (userCtx.user && site.site && mtsCtx.selectedMTS) {
        return MTSHelpers.canApproveMTS(userCtx.user, site.site, mtsCtx.selectedMTS, mtsCtx.mtsPeriods);
      }
    }

    return false;
  }, [userCtx.user, mtsCtx.selectedMTS]);

  const canSeeSubmitMTS = useMemo(() => {
    if (site.site?.showCustomReportingSubmit) {
      if (mtsCtx.selectedMTS && site.site && mtsCtx.selectedMTS?.state === DTO.MTSState.NOT_SUBMITTED) {
        return MTSHelpers.canSubmitMTS(userCtx.user, site.site, mtsCtx.selectedMTS);
      } else if (!mtsCtx.selectedMTS && site.site && site.site.zeroHoursMTSAllowed && mtsCtx.mtsPeriod) {
        return MTSHelpers.canSubmitMTS(userCtx.user, site.site, undefined, mtsCtx.mtsPeriod);
      }
    }

    return false;
  }, [mtsCtx.selectedMTS]);

  if (mtsCtx.error) {
    return <GeneralErrorMessage message={mtsCtx.error.message} />;
  } else if (!date || isNaN(date.getTime())) {
    return <GeneralErrorMessage message="Oops, something was wrong with your date" />;
  }

  return (
    <>
      <MTSToolBar />
      <CalendarMonthSubmissionStatus />
      {mtsCtx.loading && (
        <CircularProgress color="primary" />
      )}

      {!mtsCtx.loading && (
        <Grid container>
          <Grid item xs>
           {errorMessage && (
              <Grid container>
                <Grid item xs>
                  <Alert severity="error">{errorMessage}</Alert>
                </Grid>
              </Grid>
            )}

            <Paper>
              <Grid container direction='column' alignContent="flex-end" style={{ paddingTop: '16px', paddingRight: '16px' }}>
                <Grid item style={{ marginBottom: '10px' }}>
                  {(!formEditDisabled || !formHoursDisabled) && (
                    <Button variant="contained" color="primary" onClick={toggleAddModal}>
                      <LanguageString groupName="MTS" resourceName="ADD_ACTIVITY" defaultText="Add Activity" />
                    </Button>
                  )}
                </Grid>
                <Grid item>
                  {canSeeSubmitMTS && (
                    <Button variant="contained" fullWidth color="primary" onClick={toggleSubmitModal}>
                      <LanguageString groupName="MTS" resourceName="SUBMIT_MTS" defaultText="Submit MTS" />
                    </Button>
                  )}
                </Grid>
                <Grid item>
                  {canSeeApproveMTS && (
                    <Button variant="contained" fullWidth color="primary" onClick={toggleApproveModal}>
                      <LanguageString groupName="MTS" resourceName="Approve_MTS" defaultText="Approve MTS" />
                    </Button>
                  )}
                </Grid>
                <Grid item>
                  {canSeeReviewMTS && (
                    <Button variant="contained" fullWidth color="primary" onClick={toggleApproveModal}>
                      <LanguageString groupName="MTS" resourceName="Review_MTS" defaultText="Review MTS" />
                    </Button>
                  )}
                </Grid>
              </Grid>
              <Table className={classes.stripedTable}>
                {activities && activities.length > 0 ? (
                  <>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <LanguageString groupName="MTS" resourceName="ACTIVITY" defaultText="Activity" />
                        </TableCell>
                        <TableCell>
                          <LanguageString groupName="MTS" resourceName="HOURS" defaultText="Hours" />
                        </TableCell>
                        <TableCell>
                          <LanguageString groupName="MTS" resourceName="LAST_EDITED" defaultText="Last Edited By" />
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <CalendarCustomReportingRow
                        activities={activities}
                        mtsCtx={mtsCtx}
                        setAddModalOpen={setAddModalOpen}
                        setReadonlyMode={setReadonlyMode}
                        setActiveMTSEntry={setActiveMTSEntry}
                        setDeleteModalOpen={setDeleteModalOpen}
                      />
                    </TableBody>
                  </>
                ) : (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Grid container direction="column" alignItems="center" spacing={2}>
                          <Grid item>
                            <LanguageString groupName="MTS" resourceName="NO_ACTIVITIES" defaultText="No activities found" />
                          </Grid>
                        </Grid>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </Paper>

            <Dialog open={deleteModalOpen} onClose={toggleDeleteModal} aria-describedby="alert-dialog-description">
              <DialogTitle onClose={toggleDeleteModal}>
                <LanguageString groupName="MTS" resourceName="DELETE_CONFIRMATION_TITLE" defaultText="Delete Entry" />
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-description">
                  <LanguageString groupName="MTS" resourceName="DELETE_CONFIRMATION_MESSAGE" defaultText="Are you sure you'd like to delete this entry?" />
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button role="button" aria-label="cancel" onClick={toggleDeleteModal} color="primary">
                  Cancel
                </Button>
                <Button role="button" aria-label="delete" onClick={confirmDeleteMTS} color="primary" autoFocus>
                  Delete
                </Button>
              </DialogActions>
            </Dialog>

            <MTSAddActivityDialog
              open={addModalOpen}
              toggle={toggleAddModal}
              date={date}
              activityTypes={activityTypes}
              readonly={readonlyMode}
              refreshQueries={['mtsById', 'mtsUserActivityType']}
              mtsEntryId={activeMTSEntry?.id}
              mts={activteMts}
            />
            <MTSSubmitDialog
              open={submitModalOpen}
              toggle={toggleSubmitModal}
              mts={mtsCtx.selectedMTS}
              mtsPeriod={mtsCtx.mtsPeriod}
              typesNeedingApproval={typesNeedingApproval}
            />
            <MTSApproveDialog
              open={approveModalOpen}
              toggle={toggleApproveModal}
              mts={mtsCtx.selectedMTS}
              reviewingMTS={mtsCtx.selectedMTS?.state === DTO.MTSState.APPROVED ? true : false}
            />
          </Grid>
        </Grid>
      )}
      <MTSFooter />
    </>
  );
};
